package com.bikompanion.data.cache

import kotlinx.browser.localStorage
import org.w3c.dom.get
import org.w3c.dom.set

/** localStorage is shared with everything else served from the same origin. */
private const val KeyPrefix = "aparcador."

/**
 * `localStorage` rather than IndexedDB: the payload is a few tens of kilobytes against a
 * quota of several megabytes, and a synchronous get/set needs no plumbing to be shared with
 * the JVM targets.
 *
 * Writes are the fragile part — private browsing and a full quota both throw — so a failure
 * to cache is allowed to pass silently and the app carries on network-only. Reads can throw
 * too, in browsers where storage is blocked outright.
 */
internal actual fun createLocalStore(): LocalStore = BrowserLocalStore()

private class BrowserLocalStore : LocalStore {
    override fun read(key: String): String? = runCatching { localStorage[storageKey(key)] }.getOrNull()

    override fun write(
        key: String,
        value: String,
    ) {
        runCatching { localStorage[storageKey(key)] = value }
    }

    override fun remove(key: String) {
        runCatching { localStorage.removeItem(storageKey(key)) }
    }

    // Only this app's own entries, with the prefix taken back off so callers see what they wrote.
    override fun keys(): List<String> =
        runCatching {
            (0 until localStorage.length)
                .mapNotNull { localStorage.key(it) }
                .filter { it.startsWith(KeyPrefix) }
                .map { it.removePrefix(KeyPrefix) }
        }.getOrDefault(emptyList())

    private fun storageKey(key: String) = "$KeyPrefix$key"
}
